import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Partner } from "../../models/Partner.ts";
import { notification } from "../../utils/Notifications.tsx";
import DocumentItem from "./DocumentItem.tsx";
import {
  getHasMotoPartner,
  getProceedingsSummary,
  getProceedingByPartnerAndType,
} from "../../services/proceedings.service.ts";
import { findTemplatesByPositionId } from "../../services/template.service.ts";
import S3Service from "../../services/s3.service.ts";
import ExpedienteTimelineSkeleton from "./ExpedienteTimelineSkeleton.tsx";
import ExpedienteDocumentRow from "./ExpedienteDocumentRow.tsx";
import {
  expedienteListStyle,
  EXPEDIENTE_TIMELINE_KEYFRAMES,
  expedienteColors,
} from "./expedienteTimelineStyles.ts";
import ModalContainer from "../ModalContainer.tsx";
import { FileType, File } from "../../interfaces/others/ILegacyDocument.ts";
import DocumentsViewerLegacy from "./DocumentsViewerLegacy.tsx";
import { RootState } from "../../store/index.ts";
import AddNewFile from "./AddNewFile.tsx";
import EditLegacyFile from "./EditLegacyFile.tsx";
import { getProfileTabPaneClass } from "./profileTabUtils.ts";

const LEGACY_TYPE_NAMES = [
  "Foto colaborador",
  "INE frente y vuelta",
  "Comprobante de domicilio actual",
  "Original solicitud de empleo",
  "Pruebas psicométricas aplicadas por reclutamiento",
] as const;

const TimeLineDocuments = ({
  partner,
  reloadPage,
  embedded = false,
  isTabActive = false,
}: {
  partner: Partner;
  reloadPage: any;
  embedded?: boolean;
  isTabActive?: boolean;
}) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const [templatesLoading, setTemplatesLoading] = React.useState<boolean>(true);
  const [summaryLoading, setSummaryLoading] = React.useState<boolean>(false);
  const [loadingLegacyTypes, setLoadingLegacyTypes] = React.useState<
    Set<string>
  >(new Set());
  const [inmediate, setInmediate] = React.useState<any>([]);
  const [noInmediate, setNoInmediate] = React.useState<any>([]);
  const [legacyDocuments, setLegacyDocuments] = React.useState<any[]>([]);

  const [proceedingsSummary, setProceedingsSummary] = React.useState<any>({});
  const [showOldLegacyModal, setShowOldLegacyModal] =
    React.useState<boolean>(false);
  const [oldLegacyDocuments, setOldLegacyDocuments] = React.useState<
    FileType[]
  >([]);
  const [loadingOldLegacy, setLoadingOldLegacy] =
    React.useState<boolean>(false);

  const [showDocumentModal, setShowDocumentModal] =
    React.useState<boolean>(false);
  const [editingDocument, setEditingDocument] = React.useState<boolean>(false);
  const [currentFileData, setCurrentFileData] = React.useState<any[] | null>(
    null
  );
  const [currentFile, setCurrentFile] = React.useState<any | null>(null);
  const [currentFileLoading, setCurrentFileLoading] =
    React.useState<boolean>(false);
  const [recruiterNotesExpanded, setRecruiterNotesExpanded] =
    React.useState<boolean>(false);

  const [showLegacyModal, setShowLegacyModal] = React.useState<boolean>(false);
  const [currentLegacyDoc, setCurrentLegacyDoc] =
    React.useState<FileType | null>(null);
  const [currentLegacyDocName, setCurrentLegacyDocName] = React.useState<
    string | null
  >(null);

  const getIdImage = (url: string): string => {
    const pathParts = url.split("/");
    const lastPart = pathParts[pathParts.length - 1];
    const id = lastPart.split("?")[0];
    return id;
  };

  const setImages = (imagenes: any[], originalField: string): any[] => {
    const originalIndex = imagenes?.findIndex(
      (record: any) => getIdImage(record?.url) === originalField
    );

    const images = imagenes.map((record: any, index: number) => {
      return {
        id: getIdImage(record?.url),
        url: record?.url,
        original: index === originalIndex,
        lastModified: record?.lastModified || null,
      };
    });

    return images;
  };

  const setLegacyTypeLoading = (typeFile: string, loading: boolean) => {
    setLoadingLegacyTypes((prev) => {
      const next = new Set(prev);
      if (loading) {
        next.add(typeFile);
      } else {
        next.delete(typeFile);
      }
      return next;
    });
  };

  const upsertLegacyDocument = (doc: FileType) => {
    setLegacyDocuments((prev) => {
      const next = prev.filter((item) => item.typeFile !== doc.typeFile);
      if (doc.files?.length > 0) {
        return [...next, doc];
      }
      return next;
    });
  };

  const buildListsFromTemplates = (templates: any[], hasMotoData: any) => {
    let Inmediate = templates[0]?.documents ?? [];
    const NoInmediate = templates[1]?.documents ?? [];

    const transportMethod = hasMotoData?.transportMethod;
    const isMotoTransport = transportMethod === 5;
    const isCarTransport = transportMethod === 1;

    const normalize = (value?: string) => (value || "").toLowerCase().trim();
    const positionName = normalize(partner?.candidate?.vacancy?.position?.name);
    const driverPositionKeywords = ["chofer", "repartidor"];
    const driverPositionExact = ["repartidor suc", "chofer comisariato"];

    const matchesDriverKeyword = driverPositionKeywords.some((keyword) =>
      positionName.includes(keyword)
    );
    const matchesDriverExact = driverPositionExact.includes(positionName);

    const requiresLicense =
      isMotoTransport ||
      isCarTransport ||
      matchesDriverKeyword ||
      matchesDriverExact ||
      hasMotoData?.hasMoto;

    if (requiresLicense) {
      const licenseFiles = Array.isArray(hasMotoData?.fileDetails)
        ? [...hasMotoData.fileDetails]
        : [];
      const hasLicenseUploaded =
        Boolean(hasMotoData?.fileMotorcycleUploaded) ||
        licenseFiles.length > 0;

      const motoDocument = {
        id: "motoDocument",
        name: "Licencia de Conducir",
        typeFile: "Licencia de conducir",
        files: licenseFiles,
        uploaded: hasLicenseUploaded,
        uploadBy: hasMotoData.uploadBy,
        obligatory: true,
      };

      const alreadyHasLicense = Inmediate.some(
        (doc: any) => doc.id === "motoDocument"
      );

      if (!alreadyHasLicense) {
        Inmediate = [motoDocument, ...Inmediate];
      }
    }

    return { Inmediate, NoInmediate };
  };

  const fetchProceedingsSummary = async () => {
    setSummaryLoading(true);
    try {
      const procSummary = await getProceedingsSummary(partner.id);
      setProceedingsSummary(procSummary || {});
    } catch (error) {
      console.log(error);
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchLegacyTypeFromS3 = async (
    typeFile: (typeof LEGACY_TYPE_NAMES)[number],
    curp: string
  ) => {
    setLegacyTypeLoading(typeFile, true);
    try {
      let doc: FileType | null = null;

      if (typeFile === "Foto colaborador") {
        const profileUrl = await S3Service.getFiles(`PERFIL_${curp}`);
        if (profileUrl?.length > 0) {
          doc = {
            typeFile,
            files: setImages(profileUrl, `PERFIL_${curp}`),
            name: `PERFIL_${curp}`,
          };
        }
      } else if (typeFile === "INE frente y vuelta") {
        const [ineUrl, ine1Url] = await Promise.all([
          S3Service.getFiles(`INE_${curp}`),
          S3Service.getFiles(`INE_1_${curp}`),
        ]);
        const ineFiles = setImages(ineUrl, `INE_${curp}`).concat(
          setImages(ine1Url, `INE_1_${curp}`)
        );
        if (ineFiles.length > 0) {
          doc = {
            typeFile,
            files: ineFiles,
            name: `INE_${curp}`,
          };
        }
      } else if (typeFile === "Comprobante de domicilio actual") {
        const domicilioUrl = await S3Service.getFiles(`DOMICILIO_${curp}`);
        if (domicilioUrl?.length > 0) {
          doc = {
            typeFile,
            files: setImages(domicilioUrl, `DOMICILIO_${curp}`),
            name: `DOMICILIO_${curp}`,
          };
        }
      } else if (typeFile === "Original solicitud de empleo") {
        const solicitudUrl = await S3Service.getFiles(`SOLICITUD_${curp}`);
        if (solicitudUrl?.length > 0) {
          doc = {
            typeFile,
            files: setImages(solicitudUrl, `SOLICITUD_${curp}`),
            name: `SOLICITUD_${curp}`,
          };
        }
      } else if (
        typeFile === "Pruebas psicométricas aplicadas por reclutamiento"
      ) {
        const psicometricoUrl = await S3Service.getFiles(`PSICOMETRICO_${curp}`);
        if (psicometricoUrl?.length > 0) {
          doc = {
            typeFile,
            files: setImages(psicometricoUrl, `PSICOMETRICO_${curp}`),
            name: `PSICOMETRICO_${curp}`,
          };
        }
      }

      if (doc) {
        upsertLegacyDocument(doc);
      }
    } catch (error) {
      console.log(`Error fetching legacy ${typeFile}:`, error);
    } finally {
      setLegacyTypeLoading(typeFile, false);
    }
  };

  const fetchLegacyDocumentsProgressive = () => {
    const curp = partner?.person?.curp;
    if (!curp) return;

    LEGACY_TYPE_NAMES.forEach((typeFile) => {
      void fetchLegacyTypeFromS3(typeFile, curp);
    });
  };

  const fetchImages = () => {
    fetchLegacyDocumentsProgressive();
  };

  const refreshDocumentMetadata = () => {
    void fetchProceedingsSummary();
    fetchLegacyDocumentsProgressive();
  };

  const loadFiles = async () => {
    const isInitialLoad = inmediate.length === 0 && noInmediate.length === 0;
    if (isInitialLoad) {
      setTemplatesLoading(true);
    }

    try {
      const positionId = partner?.candidate?.vacancy?.position?.id;
      const [{ templates }, hasMotoData] = await Promise.all([
        findTemplatesByPositionId(positionId),
        getHasMotoPartner(partner.id),
      ]);

      const { Inmediate, NoInmediate } = buildListsFromTemplates(
        templates,
        hasMotoData
      );

      setInmediate(Inmediate);
      setNoInmediate(NoInmediate);
    } catch (error) {
      console.log(error);
      notification(
        "Error",
        "Ocurrió un error al recuperar los documentos",
        "danger"
      );
    } finally {
      setTemplatesLoading(false);
    }

    refreshDocumentMetadata();
  };

  const fetchImagesByName = async (name: string) => {
    if (!partner?.person?.curp) {
      return;
    }
    const documentsTemps: FileType[] = [...legacyDocuments];
    const curp = partner.person.curp;

    try {
      if (name.includes("PERFIL_")) {
        const profileUrl = await S3Service.getFiles(`PERFIL_${curp}`);
        const perfil =
          profileUrl && profileUrl.length > 0
            ? setImages(profileUrl, `PERFIL_${curp}`)
            : [];
        const index = documentsTemps.findIndex(
          (doc) => doc.typeFile === "Foto colaborador"
        );
        if (index > -1) {
          documentsTemps.splice(index, 1);
        }
        documentsTemps.push({
          typeFile: "Foto colaborador",
          files: perfil,
          name: `PERFIL_${curp}`,
        });
      } else if (name.includes("INE_")) {
        const ineUrl = await S3Service.getFiles(`INE_${curp}`);
        const ine = ineUrl ? setImages(ineUrl, `INE_${curp}`) : [];
        const ine1Url = await S3Service.getFiles(`INE_1_${curp}`);
        const ine1 = ine1Url ? setImages(ine1Url, `INE_1_${curp}`) : [];
        const index = documentsTemps.findIndex(
          (doc) => doc.typeFile === "INE frente y vuelta"
        );
        if (index > -1) {
          documentsTemps.splice(index, 1);
        }
        documentsTemps.push({
          typeFile: "INE frente y vuelta",
          files: ine.concat(ine1),
          name: `INE_${curp}`,
        });
      } else if (name.includes("DOMICILIO_")) {
        const domicilioUrl = await S3Service.getFiles(`DOMICILIO_${curp}`);
        const domicilio =
          domicilioUrl && domicilioUrl.length > 0
            ? setImages(domicilioUrl, `DOMICILIO_${curp}`)
            : [];
        const index = documentsTemps.findIndex(
          (doc) => doc.typeFile === "Comprobante de domicilio actual"
        );
        if (index > -1) {
          documentsTemps.splice(index, 1);
        }
        documentsTemps.push({
          typeFile: "Comprobante de domicilio actual",
          files: domicilio,
          name: `DOMICILIO_${curp}`,
        });
      } else if (name.includes("SOLICITUD_")) {
        const solicitudUrl = await S3Service.getFiles(`SOLICITUD_${curp}`);
        const solicitud =
          solicitudUrl && solicitudUrl.length > 0
            ? setImages(solicitudUrl, `SOLICITUD_${curp}`)
            : [];
        const index = documentsTemps.findIndex(
          (doc) => doc.typeFile === "Original solicitud de empleo"
        );
        if (index > -1) {
          documentsTemps.splice(index, 1);
        }
        documentsTemps.push({
          typeFile: "Original solicitud de empleo",
          files: solicitud,
          name: `SOLICITUD_${curp}`,
        });
      } else if (name.includes("PSICOMETRICO_")) {
        const psicometricoUrl = await S3Service.getFiles(
          `PSICOMETRICO_${curp}`
        );
        const psicometrico =
          psicometricoUrl && psicometricoUrl.length > 0
            ? setImages(psicometricoUrl, `PSICOMETRICO_${curp}`)
            : [];
        const index = documentsTemps.findIndex(
          (doc) =>
            doc.typeFile === "Pruebas psicométricas aplicadas por reclutamiento"
        );
        if (index > -1) {
          documentsTemps.splice(index, 1);
        }
        documentsTemps.push({
          typeFile: "Pruebas psicométricas aplicadas por reclutamiento",
          files: psicometrico,
          name: `PSICOMETRICO_${curp}`,
        });
      }
      setLegacyDocuments(documentsTemps);
    } catch (error) {
      console.log(error);
    }
  };

  const handleOpenDocumentModal = async (
    file: any,
    editing: boolean = false
  ) => {
    setCurrentFile(file);
    setEditingDocument(editing);
    setCurrentFileLoading(true);
    setShowDocumentModal(true);

    try {
      if (file.id === "motoDocument") {
        const hasMotoData = await getHasMotoPartner(partner.id);
        const updatedMotoFiles = hasMotoData.fileMotorcycleUploaded
          ? [...hasMotoData.fileDetails]
          : [];
        const motoDataList = updatedMotoFiles.map((motoFile) => ({
          id: file.id,
          fields: [],
          createdAt: new Date().toISOString(),
          updateAt: new Date().toISOString(),
          documentType: {
            id: file.id,
            name: file.name,
            fields: [],
          },
          file: motoFile,
          uploadBy: hasMotoData.uploadBy,
          url: motoFile.url,
        }));
        setCurrentFileData(motoDataList);
      } else {
        const data = await getProceedingByPartnerAndType(partner.id, file.id);
        setCurrentFileData(data);
      }
    } catch (error) {
      console.error(error);
      notification(
        "Error",
        "Ocurrió un error al recuperar los documentos",
        "danger"
      );
    } finally {
      setCurrentFileLoading(false);
    }
  };

  const handleCloseDocumentModal = () => {
    setShowDocumentModal(false);
    setCurrentFile(null);
    setCurrentFileData(null);
    setEditingDocument(false);
  };

  const handleOpenLegacyModal = (legacyDoc: FileType) => {
    // Si el documento no tiene name, intentar obtenerlo del tipo de documento
    let docName = legacyDoc.name;
    if (!docName && legacyDoc.typeFile && partner?.person?.curp) {
      const curp = partner.person.curp;
      // Mapear el tipo de documento al nombre esperado
      const nameMap: { [key: string]: string } = {
        "Foto colaborador": `PERFIL_${curp}`,
        "INE frente y vuelta": `INE_${curp}`,
        "Comprobante de domicilio actual": `DOMICILIO_${curp}`,
        "Original solicitud de empleo": `SOLICITUD_${curp}`,
        "Pruebas psicométricas aplicadas por reclutamiento": `PSICOMETRICO_${curp}`,
      };
      docName = nameMap[legacyDoc.typeFile] || "";
    }

    setCurrentLegacyDoc(legacyDoc);
    setCurrentLegacyDocName(docName || null);
    setShowLegacyModal(true);
  };

  const handleCloseLegacyModal = () => {
    setShowLegacyModal(false);
    setCurrentLegacyDoc(null);
    setCurrentLegacyDocName(null);
  };

  // Actualizar currentLegacyDoc cuando legacyDocuments cambie
  React.useEffect(() => {
    if (showLegacyModal && currentLegacyDocName) {
      const updatedDoc = legacyDocuments.find(
        (doc) => doc.name === currentLegacyDocName
      );
      if (updatedDoc) {
        setCurrentLegacyDoc(updatedDoc);
      }
    }
  }, [legacyDocuments, showLegacyModal, currentLegacyDocName]);

  const fetchOldLegacyDocuments = async () => {
    if (!partner?.person?.curp) {
      notification("Error", "No se encontró el CURP del colaborador", "danger");
      return;
    }

    setLoadingOldLegacy(true);
    setShowOldLegacyModal(true);
    const documentsTemps: FileType[] = [];
    const curp = partner.person.curp;

    try {
      const profileUrl = await S3Service.getFiles(`PERFIL_${curp}`);
      if (profileUrl && profileUrl.length > 0) {
        const perfil = setImages(profileUrl, `PERFIL_${curp}`);
        documentsTemps.push({
          typeFile: "Foto colaborador",
          files: perfil,
          name: `PERFIL_${curp}`,
        });
      }

      const ineUrl = await S3Service.getFiles(`INE_${curp}`);
      const ine1Url = await S3Service.getFiles(`INE_1_${curp}`);
      const ine = setImages(ineUrl, `INE_${curp}`);
      const ine1 = setImages(ine1Url, `INE_1_${curp}`);
      const ineFiles = ine.concat(ine1);
      if (ineFiles.length > 0) {
        documentsTemps.push({
          typeFile: "INE frente y vuelta",
          files: ineFiles,
          name: `INE_${curp}`,
        });
      }

      const domicilioUrl = await S3Service.getFiles(`DOMICILIO_${curp}`);
      if (domicilioUrl && domicilioUrl.length > 0) {
        const domicilio = setImages(domicilioUrl, `DOMICILIO_${curp}`);
        documentsTemps.push({
          typeFile: "Comprobante de domicilio actual",
          files: domicilio,
          name: `DOMICILIO_${curp}`,
        });
      }

      const solicitudUrl = await S3Service.getFiles(`SOLICITUD_${curp}`);
      if (solicitudUrl && solicitudUrl.length > 0) {
        const solicitud = setImages(solicitudUrl, `SOLICITUD_${curp}`);
        documentsTemps.push({
          typeFile: "Original solicitud de empleo",
          files: solicitud,
          name: `SOLICITUD_${curp}`,
        });
      }

      const psicometricoUrl = await S3Service.getFiles(`PSICOMETRICO_${curp}`);
      if (psicometricoUrl && psicometricoUrl.length > 0) {
        const psicometrico = setImages(psicometricoUrl, `PSICOMETRICO_${curp}`);
        documentsTemps.push({
          typeFile: "Pruebas psicométricas aplicadas por reclutamiento",
          files: psicometrico,
          name: `PSICOMETRICO_${curp}`,
        });
      }

      setOldLegacyDocuments(documentsTemps);
    } catch (error) {
      console.error("Error fetching old legacy documents:", error);
      notification(
        "Error",
        "Ocurrió un error al recuperar los documentos anteriores",
        "danger"
      );
    } finally {
      setLoadingOldLegacy(false);
    }
  };

  React.useEffect(() => {
    loadFiles();
  }, []);

  return (
    <div
      className={
        embedded
          ? "profile-timeline-embedded"
          : getProfileTabPaneClass(isTabActive)
      }
      id="expediente"
      style={{ paddingTop: 0, marginTop: 0 }}
    >
      <style>{EXPEDIENTE_TIMELINE_KEYFRAMES}</style>
      <div style={{ position: "relative" }}>
        {templatesLoading ? (
          <ExpedienteTimelineSkeleton count={8} />
        ) : (
        <>
        <div style={expedienteListStyle}>
          {(() => {
            const hasNotes = Boolean(partner?.candidate?.notasReclutador);
            const notesOffset = hasNotes ? 1 : 0;
            const totalItems =
              notesOffset + inmediate.length + noInmediate.length;

            return (
              <>
                {hasNotes && (
                  <ExpedienteDocumentRow
                    title="Notas del reclutador"
                    showBadge={true}
                    badgeLabel="Notas"
                    obligatory={false}
                    hasFiles={true}
                    expanded={recruiterNotesExpanded}
                    onToggleExpand={() =>
                      setRecruiterNotesExpanded(!recruiterNotesExpanded)
                    }
                    isLast={totalItems === 1}
                    animationDelay={0}
                    expandedContent={
                      <textarea
                        className="form-control"
                        rows={4}
                        value={partner?.candidate?.notasReclutador || ""}
                        disabled
                        style={{
                          resize: "none",
                          border: `1px solid ${expedienteColors.cardBorder}`,
                          borderRadius: 8,
                          fontSize: 14,
                          backgroundColor: "#fff",
                        }}
                      />
                    }
                  />
                )}
                {inmediate.map((record, index) => {
                  const rowIndex = notesOffset + index;
                  return (
                    <DocumentItem
                      key={`inm-${record.id ?? record.name}-${index}`}
                      partnerId={partner.id}
                      file={record}
                      documentsArray={legacyDocuments}
                      proceedingsSummary={proceedingsSummary}
                      summaryLoading={summaryLoading}
                      legacyLoading={loadingLegacyTypes.has(record.name)}
                      onEditClick={handleOpenDocumentModal}
                      onLegacyEditClick={handleOpenLegacyModal}
                      isLast={rowIndex === totalItems - 1}
                      animationDelay={rowIndex * 0.05}
                    />
                  );
                })}
                {noInmediate.map((record, index) => {
                  const rowIndex = notesOffset + inmediate.length + index;
                  return (
                    <DocumentItem
                      key={`noinm-${record.id ?? record.name}-${index}`}
                      partnerId={partner.id}
                      file={record}
                      documentsArray={legacyDocuments}
                      proceedingsSummary={proceedingsSummary}
                      summaryLoading={summaryLoading}
                      legacyLoading={loadingLegacyTypes.has(record.name)}
                      onEditClick={handleOpenDocumentModal}
                      onLegacyEditClick={handleOpenLegacyModal}
                      isLast={rowIndex === totalItems - 1}
                      animationDelay={rowIndex * 0.05}
                    />
                  );
                })}
              </>
            );
          })()}
        </div>
        </>
        )}
      </div>

      <ModalContainer
        open={showDocumentModal}
        title="Subir documento"
        size="lg"
        closeFn={handleCloseDocumentModal}
      >
        {currentFileLoading ? (
          <div className="text-center p-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
          </div>
        ) : currentFile ? (
          <AddNewFile
            partner={partner.id}
            fields={currentFile?.fields || []}
            fileTypeId={currentFile?.id}
            loadFiles={loadFiles}
            partnerObject={partner}
            initialValues={
              currentFileData && currentFileData.length > 0
                ? currentFileData[currentFileData.length - 1].fields.reduce(
                    (acc: any, field: any) => {
                      acc[field.field] = field.value;
                      return acc;
                    },
                    {}
                  )
                : {}
            }
            initialFiles={currentFileData}
            editing={editingDocument}
            closeFn={handleCloseDocumentModal}
            setLoading={setCurrentFileLoading}
            reloadPage={reloadPage}
            refetch={async () => {
              try {
                setCurrentFileLoading(true);
                refreshDocumentMetadata();
                if (currentFile) {
                  const data = await getProceedingByPartnerAndType(
                    partner.id,
                    currentFile.id
                  );
                  setCurrentFileData(data);
                }
              } finally {
                setCurrentFileLoading(false);
              }
            }}
          />
        ) : null}
      </ModalContainer>

      {/* Modal para documentos legacy */}
      <ModalContainer
        open={showLegacyModal}
        title={currentLegacyDoc?.typeFile || "Documento"}
        size="lg"
        closeFn={handleCloseLegacyModal}
      >
        {currentLegacyDoc ? (
          <EditLegacyFile
            partnerObject={partner}
            file={currentLegacyDoc}
            closeFn={async () => {
              handleCloseLegacyModal();
              const docName = currentLegacyDocName || currentLegacyDoc.name;
              if (docName) {
                await fetchImagesByName(docName);
                // Actualizar currentLegacyDoc con los datos actualizados después de cerrar
                const updatedLegacyDoc = legacyDocuments.find(
                  (doc) => doc.name === docName
                );
                if (updatedLegacyDoc) {
                  setCurrentLegacyDoc(updatedLegacyDoc);
                }
              }
              // No llamar a loadFiles() para evitar recargar todos los documentos
            }}
            refetch={async (name) => {
              const docName =
                name || currentLegacyDocName || currentLegacyDoc.name;
              if (docName) {
                await fetchImagesByName(docName);
                // Actualizar currentLegacyDoc con los datos actualizados
                const updatedLegacyDoc = legacyDocuments.find(
                  (doc) => doc.name === docName
                );
                if (updatedLegacyDoc) {
                  setCurrentLegacyDoc(updatedLegacyDoc);
                }
              }
              // No llamar a loadFiles() para evitar recargar todos los documentos
            }}
          />
        ) : null}
      </ModalContainer>

      {/* Modal para documentos anteriores */}
      <ModalContainer
        open={showOldLegacyModal}
        title="Documentos anteriores"
        size="lg"
        closeFn={() => {
          setShowOldLegacyModal(false);
          setOldLegacyDocuments([]);
        }}
      >
        {loadingOldLegacy ? (
          <div className="text-center p-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
          </div>
        ) : oldLegacyDocuments.length === 0 ? (
          <div className="text-center p-5">
            <p className="text-muted">
              No se encontraron documentos anteriores.
            </p>
          </div>
        ) : (
          <div>
            {oldLegacyDocuments.map((legacyDoc, index) => (
              <div key={`old-legacy-${index}`}>
                <div className="card border mb-4 shadow-sm">
                  <div className="card-body p-4">
                    <div className="timeline-content">
                      <div className="timeline-header bg-light p-3 rounded mb-3">
                        <div className="username fs-1">
                          <p className="mb-0 fw-bold">{legacyDoc.typeFile}</p>
                        </div>
                      </div>
                      <div className="timeline-body">
                        <div className="mb-3">
                          <div className="row">
                            {legacyDoc.files.length > 0 ? (
                              <DocumentsViewerLegacy
                                fileData={legacyDoc.files}
                              />
                            ) : (
                              <div className="d-flex align-items-center">
                                <p className="text-muted mb-0">
                                  No hay archivos disponibles
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {index < oldLegacyDocuments.length - 1 && (
                  <hr className="my-4" />
                )}
              </div>
            ))}
          </div>
        )}
      </ModalContainer>
    </div>
  );
};

export default TimeLineDocuments;
